"use client";

import type React from "react";

import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import clsx from "clsx";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { useTranslations } from "next-intl";
import { Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { Button } from "./Button";

import { EventName } from "@mw/shared/analyticsEventNames";

import { Link } from "./Link";
import { LinkTracked } from "./LinkTracked";
import { cn } from "@ui/lib/utils";

export interface FeatureItem {
  icon: React.ReactNode;
  text: string;
}

/**
 * The "sign up to keep going" value-prop bullet list. Rendered inside the
 * popover below, and standalone by surfaces that can't stack a dialog (e.g. the
 * flow-template gallery's modal panel) so the badge styling stays in sync.
 */
export function SignUpFeatureList({ items, className }: { items: FeatureItem[]; className?: string }) {
  return (
    <ul className={cn("tw-flex tw-flex-col tw-gap-3", className)}>
      {items.map(item => (
        <li key={item.text} className='tw-flex tw-gap-3 tw-items-center'>
          <div
            className={clsx(
              "tw-size-9 tw-shrink-0 tw-rounded-[10px]",
              "tw-bg-paper tw-border tw-border-black/[0.16]",
              "tw-flex tw-items-center tw-justify-center",
            )}
          >
            <span className='tw-size-[18px] tw-flex tw-items-center tw-justify-center'>{item.icon}</span>
          </div>
          <p className='tw-type-base tw-flex-1'>{item.text}</p>
        </li>
      ))}
    </ul>
  );
}

export interface SignUpPopoverProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  cover?: React.ReactNode;
  title: string;
  featureItems: [FeatureItem, ...FeatureItem[]];
  ctaText: string;
  ctaHref: string;
  ctaEventName?: EventName;
  ctaEventProperties?: Record<string, unknown>;
}

const SignUpPopover = ({
  isOpen,
  onOpenChange,
  cover,
  title,
  featureItems,
  ctaText,
  ctaHref,
  ctaEventName,
  ctaEventProperties,
}: SignUpPopoverProps) => {
  const t = useTranslations("ClientKeys.Globals");
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className={({ isEntering, isExiting }) =>
        clsx(
          "tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-backdrop-blur-[3px]",
          isEntering && "tw-animate-in tw-fade-in tw-duration-200",
          isExiting && "tw-animate-out tw-fade-out tw-duration-200",
        )
      }
    >
      <Modal
        className={({ isEntering, isExiting }) =>
          clsx(
            "tw-relative tw-w-[440px] tw-max-w-[calc(100vw-40px)]",
            "tw-rounded-[28px] tw-overflow-hidden",
            "tw-bg-paper tw-shadow-sm",
            "tw-outline-none",
            isEntering && "tw-animate-in tw-fade-in tw-zoom-in-95 tw-duration-200",
            isExiting && "tw-animate-out tw-fade-out tw-zoom-out-95 tw-duration-200",
          )
        }
      >
        <Dialog className='tw-outline-none'>
          {/* Cover slot */}
          {cover && (
            <div className='tw-h-[178px] tw-w-full tw-overflow-hidden tw-shrink-0' inert>
              {cover}
            </div>
          )}

          {/* Close button — inside the card, top-right corner */}
          <Button
            slot='close'
            variant='elevated'
            size='pill-responsive'
            className={cn("tw-absolute tw-top-2 tw-right-2", "tw-flex tw-items-center tw-justify-center")}
            aria-label={t("close")}
            onClick={() => onOpenChange(false)}
          >
            <IconCrossLarge className={clsx(iconStrokeFix, "tw-size-4 tw-text-black")} />
          </Button>

          {/* Body */}
          <div className='tw-flex tw-flex-col tw-gap-6 tw-p-4 sm:tw-gap-9 sm:tw-p-6'>
            {/* Title */}
            <Heading slot='title' className='tw-type-3xl tw-font-display tw-font-light tw-tracking-normal tw-w-full'>
              {title}
            </Heading>

            {/* Feature list */}
            <SignUpFeatureList items={featureItems} className='sm:tw-gap-6' />

            {/* CTA */}
            {ctaEventName ? (
              <LinkTracked
                asButton
                buttonVariants={{ variant: "primary", size: "lg" }}
                className='tw-w-full tw-h-[60px] tw-rounded-full tw-justify-center'
                href={ctaHref}
                eventName={ctaEventName}
                eventProperties={ctaEventProperties}
              >
                {ctaText}
              </LinkTracked>
            ) : (
              <Link
                asButton
                buttonVariants={{ variant: "primary", size: "lg" }}
                className='tw-w-full tw-h-[60px] tw-rounded-full tw-justify-center'
                href={ctaHref}
              >
                {ctaText}
              </Link>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

SignUpPopover.displayName = "SignUpPopover";

export { SignUpPopover };
